<template>
  <div v-accessible-feed class="image-feed" role="feed" :aria-busy="loading">
    <template
      v-for="(image, index) in images"
    >
      <EImageResult
        :key="`${image.imageID}-${index}`"
        :image="image"
        :style="itemStyle(image)"
        :aria-posinset="index + 1"
        :aria-setsize="images.length"
        :lazy-load="index > resultsPerPage"
        :preview-open="index === previewOpenIndex"
        :is-first="index === 0"
        :is-last="index === images.length - 1"
        class="image-feed__item"
        data-test-id="images-result"
        tabindex="0"
        @preview-toggle="togglePreview(index)"
        @preview-close="closePreview"
        @preview-previous="goToPreviousPreview"
        @preview-next="goToNextPreview"
      />
      <hr
        v-if="index < images.length - 1"
        :key="`${image.imageID}-${index}-divider`"
        class="image-feed__divider"
      >
    </template>
    <template
      v-for="fillerIndex in fillerCount"
    >
      <hr
        :key="`filler-divider-${fillerIndex}`"
        class="image-feed__divider"
      >
      <div
        :key="`filler-${fillerIndex}`"
        class="image-feed__item image-feed__item--filler"
        role="presentation"
      />
    </template>
    <ELoading
      v-show="loadingShown"
      class="image-feed__loading"
      data-test-id="image-feed-loading"
    />
    <div
      v-if="fetchError"
      class="image-feed__fetch-error"
      role="alert"
      data-test-id="image-feed-fetch-error"
    >
      <EIcon icon="exclamation-circle" size="s" class="image-feed__error-icon" />
      {{ $t('common.generic.error') }}.
      <ELink data-test-id="image-feed-fetch-error-link" @click="retryLoadingPage">
        {{ $t('common.generic.tryagain') }}
      </ELink>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import { sendEvent } from '~~/common/js/client/analytics';
import { getHashParam, setHashParam, deleteHashParam, IMAGE_ID_KEY } from '~~/search/common/js/universal/hash-params';
import ELink from '~~/common/vue/components/link';
import EIcon from '~~/common/vue/components/icon';
import ELoading from '~~/common/vue/components/loading';

import {
  RESULTS_PER_PAGE_MOBILE,
  RESULTS_PER_PAGE_DESKTOP,
} from '../constants';
import EImageResult from './image-result';

const MAX_ITEMS_PER_ROW = 6;
// if viewing the image preview on mobile
// this many images before the last one, we start
// loading the next page
export const NEXT_PAGE_LOAD_HEAD_START = 3;

export default {
  name: 'EImageFeed',
  components: {
    EIcon,
    EImageResult,
    ELink,
    ELoading,
  },
  props: {
    images: {
      type: Array,
      required: true,
    },
    pages: {
      type: Number,
      required: true,
    },
    totalResults: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    lastPageLoaded: null,
    loading: false,
    previewOpenIndex: null,
  }),
  computed: {
    ...mapState('user', ['deviceType']),
    ...mapState(['fetchError', 'endOfFeed']),
    resultsPerPage() {
      return this.deviceType === 'mobile' ?
        RESULTS_PER_PAGE_MOBILE :
        RESULTS_PER_PAGE_DESKTOP;
    },
    fillerCount() {
      return MAX_ITEMS_PER_ROW - (this.images.length % MAX_ITEMS_PER_ROW);
    },
    loadingShown() {
      return !this.endOfFeed &&
        this.lastPageLoaded !== null &&
        this.lastPageLoaded < this.pages && !this.fetchError;
    },
  },
  mounted() {
    this.initInfiniteScroll();
    // this activates the loading-animation component,
    // meaning it won't be visible if JS is switched off
    this.lastPageLoaded = 0;
    // open preview for a given image if hash param present
    const previewId = getHashParam(IMAGE_ID_KEY);
    this.previewOpenIndex = this.getImageIndexForId(previewId);
  },
  methods: {
    ...mapMutations(['addResults', 'setEndOfFeed', 'setFetchError']),
    ...mapActions(['fetchNextPage']),
    getImageIndexForId(id) {
      return this.images.findIndex(({ imageID }) => imageID === id);
    },
    initInfiniteScroll() {
      if (!window.IntersectionObserver) {
        this.lastPageLoaded = null;
        return;
      }
      // if the user scrolls to this amount of pixels from the end of the feed,
      // we already start loading the next batch of images
      const infiniteScrollTolerance = window.innerHeight / 2;
      const observer = new IntersectionObserver(this.intersectionObserved, {
        rootMargin: `${infiniteScrollTolerance}px 0px ${infiniteScrollTolerance}px 0px`,
      });
      observer.observe(this.$el.lastElementChild);
    },
    itemStyle({ thumbnail: { height, width } }) {
      return `--feed-item-width: ${width}; --feed-item-height: ${height};`;
    },
    getLastPageUrl() {
      const url = new URL(location.href);
      url.searchParams.set('offset', this.images.length);
      url.searchParams.set('p', this.lastPageLoaded);
      url.searchParams.delete('expansionOf');
      return url.toString();
    },
    async loadPage() {
      this.loading = true;
      await this.fetchNextPage(this.getLastPageUrl());
      sendEvent('images_infinitescroll_page_loaded', { page: this.lastPageLoaded });
      this.loading = false;
    },
    loadNextPage() {
      this.lastPageLoaded += 1;
      if (this.images.length >= this.totalResults) {
        this.setEndOfFeed();
        return;
      }
      this.loadPage();
    },
    retryLoadingPage() {
      this.setFetchError(false);
      this.loadPage();
    },
    intersectionObserved([entry]) {
      if (entry.isIntersecting) {
        this.loadNextPage();
      }
    },
    togglePreview(index) {
      if (index !== this.previewOpenIndex) {
        this.openPreview(index);
      } else {
        this.closePreview();
      }
    },
    openPreview(index) {
      this.previewOpenIndex = index;
      const image = this.images[index];
      if (image) {
        setHashParam(IMAGE_ID_KEY, image.imageID);
      }
    },
    closePreview() {
      this.previewOpenIndex = null;
      deleteHashParam(IMAGE_ID_KEY);
    },
    goToPreviousPreview() {
      this.previewOpenIndex = Math.max(0, this.previewOpenIndex - 1);
    },
    goToNextPreview() {
      this.previewOpenIndex = Math.min(this.images.length - 1, this.previewOpenIndex + 1);
      if (this.previewOpenIndex >= this.images.length - NEXT_PAGE_LOAD_HEAD_START) {
        this.loadNextPage();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$item-spacing-mobile-half: $space-s / 2;
$item-spacing-desktop-half: $space-m / 2;

.image-feed {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  margin: 0 $item-spacing-mobile-half;

  @include desktop {
    margin: 0 $item-spacing-desktop-half;
  }
}

.image-feed__item {
  padding: $item-spacing-mobile-half;
  flex-basis: 0%;
  flex-shrink: 1;
  flex-grow: calc(var(--feed-item-width) / var(--feed-item-height));
  overflow: hidden;

  &:focus {
    // the thumbnails have a special focus state already
    outline: none;
  }

  @include desktop {
    padding: $item-spacing-desktop-half;
  }
}

.image-feed__item--filler {
  flex-grow: 1;
}

.image-feed__divider {
  @include flexbox-divider(2, (tablet: 4, desktop: 5, desktop-l: 6));
}

.image-feed__loading {
  flex-basis: 100%;
}

.image-feed__fetch-error {
  margin: $space-1l auto 0;
  text-align: center;
  width: 100%;
}

.image-feed__error-icon {
  color: $gray-40;
  margin-right: $space-2s;
}
</style>
